import type { JSX, ReactNode } from 'react'

import clsx from 'clsx'

import { Skeleton, Text } from '@buffer-mono/popcorn'

import { ChartErrorState } from '../../components/ChartErrorState'
import {
  BarChart,
  BreakdownBarChart,
  ChartLegendView,
  ChartSeriesProvider,
  NetChangeBarChart,
  TimeSeriesChart,
} from '../../components/charts'
import type { ChartSeriesInput } from '../../components/charts'
import { StackedBarChart } from '../../components/MetricChart'
import { SectionCard } from '../../components/SectionCard'

import { ChannelLegend } from './ChannelLegend'
import {
  ChartViewToggle,
  ExcludedServicesNotice,
  FOLLOWERS_VIEW_DROPDOWN_OPTIONS,
  FOLLOWERS_VIEW_OPTIONS,
  FollowersView,
  isFollowersView,
  isPostsView,
  POSTS_VIEW_DROPDOWN_OPTIONS,
  POSTS_VIEW_OPTIONS,
} from './ChartViewToggle'
import type { PostsView } from './ChartViewToggle'
import type { toPostsStackedBarSeries } from './toPostsStackedBarSeries'
import { useFollowersAnnotations } from './useFollowersAnnotations'
import type { useMetricChannels } from './useMetricChannels'
import styles from './MetricsSection.module.css'

type MetricChannels = ReturnType<typeof useMetricChannels>

type ChartSectionViewProps = Readonly<{
  metricChannels: MetricChannels
  totalChannelCount: number
  startDate: Date
  endDate: Date
  dateLabel: ReactNode
  loading?: boolean
  error?: unknown
  refreshing?: boolean
  emptyState: ReactNode
  headerExtraActions?: ReactNode
  toggleLabel?: string
}>

function ChartSectionHeader({
  title,
  metricChannels,
  totalChannelCount,
  dateLabel,
  actions,
}: Readonly<{
  title: string
  metricChannels: MetricChannels
  totalChannelCount: number
  dateLabel: ReactNode
  actions: ReactNode
}>): JSX.Element {
  const { displayedChannels, showChannelCountNotice } = metricChannels
  return (
    <SectionCard.Header>
      <SectionCard.Title>{title}</SectionCard.Title>
      <SectionCard.Actions>{actions}</SectionCard.Actions>
      <SectionCard.Subtitle>
        <Text size="sm" color="subtle">
          {dateLabel}
          {showChannelCountNotice && (
            <>
              {' · '}
              <Text as="span" size="sm" color="warning">
                Showing {displayedChannels.length} of {totalChannelCount}{' '}
                channels. Filter by channel to see a different set.
              </Text>
            </>
          )}
        </Text>
      </SectionCard.Subtitle>
    </SectionCard.Header>
  )
}

export function FollowersChartSectionView({
  metricChannels,
  totalChannelCount,
  series,
  view,
  onViewChange,
  startDate,
  endDate,
  dateLabel,
  loading = false,
  error,
  refreshing = false,
  emptyState,
  headerExtraActions,
  toggleLabel,
}: ChartSectionViewProps &
  Readonly<{
    series: ChartSeriesInput[]
    view: FollowersView
    onViewChange: (view: FollowersView) => void
  }>): JSX.Element {
  const { displayedChannels, channels, excludedServiceNames } = metricChannels
  const { followersAnnotations, followersAnnotationTicks } =
    useFollowersAnnotations({ displayedChannels, channels, startDate, endDate })

  const hasAnyData = displayedChannels.length > 0
  const showViewToggle = hasAnyData && !loading && !error

  const excludedNotice = (
    <ExcludedServicesNotice serviceNames={excludedServiceNames}>
      profiles don&apos;t report followers, so they are excluded from this
      chart.
    </ExcludedServicesNotice>
  )

  const content = loading ? (
    <Skeleton className={styles.skeletonChart} />
  ) : error ? (
    <div className={styles.emptyState}>
      <ChartErrorState />
    </div>
  ) : !hasAnyData ? (
    <>
      <div className={styles.chartWrapper}>
        <div className={styles.chartContainer}>
          <ChartSeriesProvider series={[]}>
            <TimeSeriesChart
              ariaLabel="Followers over time by channel"
              singleAxis
              yAxisLabel="Followers"
              startDate={startDate}
              endDate={endDate}
              emptyState={emptyState}
            />
          </ChartSeriesProvider>
        </div>
      </div>
      {excludedNotice}
    </>
  ) : (
    <>
      <div
        className={clsx(styles.chartWrapper, refreshing && styles.refreshing)}
        aria-busy={refreshing}
        translate="no"
      >
        <ChartSeriesProvider series={series}>
          <div className={styles.chartContainer}>
            {view === FollowersView.OVER_TIME ? (
              <TimeSeriesChart
                ariaLabel="Followers over time by channel"
                singleAxis
                showIconsInTooltip
                yAxisLabel="Followers"
                startDate={startDate}
                endDate={endDate}
                annotations={followersAnnotations}
                xAxisTicks={followersAnnotationTicks}
                enableNoDataHover
              />
            ) : view === FollowersView.BY_CHANNEL ? (
              <BreakdownBarChart ariaLabel="Followers by channel" />
            ) : (
              <NetChangeBarChart ariaLabel="Net follower change by channel" />
            )}
            {/* Only the Line view needs a colour key; Bar/Growth label each row
                on the axis. Inside the container so the chart padding sits below
                the legend, not between it and the chart. */}
            {view === FollowersView.OVER_TIME && (
              <div className={styles.chartLegend}>
                <ChartLegendView ariaLabel="Followers legend" />
              </div>
            )}
          </div>
        </ChartSeriesProvider>
      </div>
      {excludedNotice}
    </>
  )

  return (
    <SectionCard>
      <ChartSectionHeader
        title="Followers"
        metricChannels={metricChannels}
        totalChannelCount={totalChannelCount}
        dateLabel={dateLabel}
        actions={
          hasAnyData ? (
            <div className={styles.headerActions}>
              {showViewToggle && (
                <ChartViewToggle
                  value={view}
                  onChange={onViewChange}
                  isValue={isFollowersView}
                  options={FOLLOWERS_VIEW_OPTIONS}
                  dropdownOptions={FOLLOWERS_VIEW_DROPDOWN_OPTIONS}
                  aria-label={toggleLabel}
                />
              )}
              {headerExtraActions}
            </div>
          ) : undefined
        }
      />
      {content}
    </SectionCard>
  )
}

export function PostsChartSectionView({
  metricChannels,
  totalChannelCount,
  stackedSeries,
  barSeries,
  view,
  onViewChange,
  startDate,
  endDate,
  dateLabel,
  loading = false,
  error,
  refreshing = false,
  emptyState,
  headerExtraActions,
  toggleLabel,
}: ChartSectionViewProps &
  Readonly<{
    stackedSeries: ReturnType<typeof toPostsStackedBarSeries>
    barSeries: ChartSeriesInput[]
    view: PostsView
    onViewChange: (view: PostsView) => void
  }>): JSX.Element {
  const { channels, excludedServiceNames } = metricChannels
  const stacked = view === 'stackedBar'

  // A channel with only zero-valued days has no posts to chart, so a set of
  // all-zero series is treated as "no data": show the empty state instead of a
  // chart of empty bars. @see CORE-6428
  const hasMeaningfulData = stackedSeries.some((series) =>
    series.data.some((point) => point.value != null && point.value !== 0),
  )

  const showViewToggle = hasMeaningfulData && !loading && !error

  const excludedNotice = (
    <ExcludedServicesNotice serviceNames={excludedServiceNames}>
      don&apos;t have posts, so they are excluded from this chart.
    </ExcludedServicesNotice>
  )

  const barChart = stacked ? (
    <StackedBarChart
      series={stackedSeries}
      yAxisLabel="Posts"
      startDate={startDate}
      endDate={endDate}
      showIcons
      ariaLabel="Posts over time by channel"
    />
  ) : (
    <ChartSeriesProvider series={barSeries}>
      <BarChart ariaLabel="Posts by channel" xAxisLabel="Posts sent" />
    </ChartSeriesProvider>
  )

  const content = loading ? (
    <Skeleton className={styles.skeletonChart} />
  ) : error ? (
    <div className={styles.emptyState}>
      <ChartErrorState />
    </div>
  ) : !hasMeaningfulData ? (
    <>
      {/* Render the empty state on its own, not through the BarChart's overlay —
          the chart would otherwise draw its (empty) axes behind it. @see CORE-6428 */}
      <div className={styles.chartWrapper}>
        <div className={styles.chartContainer}>
          <div className={styles.emptyState}>{emptyState}</div>
        </div>
      </div>
      {excludedNotice}
    </>
  ) : (
    <>
      <div
        className={clsx(styles.chartWrapper, refreshing && styles.refreshing)}
        aria-busy={refreshing}
        translate="no"
      >
        <div className={styles.chartContainer}>
          {barChart}
          {/* Only Stacked needs a colour key; the ranked Bar view labels each
              channel on the axis. Inside the container so the chart padding sits
              below the legend, not between it and the chart. */}
          {stacked && (
            <div className={styles.chartLegend}>
              <ChannelLegend channels={channels} />
            </div>
          )}
        </div>
      </div>
      {excludedNotice}
    </>
  )

  return (
    <SectionCard>
      <ChartSectionHeader
        title="Posts"
        metricChannels={metricChannels}
        totalChannelCount={totalChannelCount}
        dateLabel={dateLabel}
        actions={
          hasMeaningfulData ? (
            <div className={styles.headerActions}>
              {showViewToggle && (
                <ChartViewToggle
                  value={view}
                  onChange={onViewChange}
                  isValue={isPostsView}
                  options={POSTS_VIEW_OPTIONS}
                  dropdownOptions={POSTS_VIEW_DROPDOWN_OPTIONS}
                  aria-label={toggleLabel}
                />
              )}
              {headerExtraActions}
            </div>
          ) : undefined
        }
      />
      {content}
    </SectionCard>
  )
}
